import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Autoplay, A11y, Grid } from "swiper";
import "swiper/swiper-bundle.css";
import sal from "sal.js";
import api from "../../../services/api";
import toast from "react-hot-toast";
import DnaOverlayLoader from "../../loader/DnaOverlayLoader";

SwiperCore.use([Navigation, Autoplay, A11y, Grid]);

const MedicalDepartments = ({ formRef }) => {
  const progressData = [
    { img: "/assets/img/spacial-icon-01.webp", title: "Gastroenterology", delay: 100 },
    { img: "/assets/img/spacial-icon-02.webp", title: "Orthopedics", delay: 200 },
    { img: "/assets/img/spacial-icon-03.webp", title: "Neurology", delay: 300 },
    { img: "/assets/img/spacial-icon-05.png", title: "Abdominal Cavity", delay: 400 },
    { img: "/assets/img/spacial-icon-06.png", title: "Endocrinology", delay: 500 },
    { img: "/assets/img/spacial-icon-07.png", title: "Bariatrics / Metabolics", delay: 600 },
    { img: "/assets/img/spacial-icon-08.png", title: "General Surgery", delay: 700 },
    { img: "/assets/img/spacial-icon-09.png", title: "Urology", delay: 800 },
  ];

  const [formLoading, setFormLoading] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    age: "",
    gender: "",
    email: "",
    country: "",
    phone: "",
    speciality: "",
    comments: "",
  });

  /* ================= VALIDATORS ================= */
  const validators = {
    name: (val) => val.replace(/[^a-zA-Z\s]/g, "").slice(0, 50),
    age: (val) => val.replace(/[^0-9]/g, "").slice(0, 3),
    phone: (val) => val.replace(/[^0-9]/g, "").slice(0, 12),
    email: (val) => val.replace(/\s/g, "").slice(0, 75),
    comments: (val) => val.slice(0, 250),
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;
    if (validators[name]) {
      newValue = validators[name](value);
    }

    setFormData({ ...formData, [name]: newValue });
  };

  /* ================= FETCH SPECIALTIES ================= */
  useEffect(() => {
    const fetchSpecialties = async () => {
      try {
        const response = await api.get("/api/specialties-name");
        setSpecialties(response.data);
      } catch (error) {
        console.error("Failed to load specialties:", error);
        toast.error("Failed to load specialties");
      } finally {
        setLoadingSpecialties(false);
      }
    };

    fetchSpecialties();
  }, []);

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Final validation safety
    if (!/^[A-Za-z\s]+$/.test(formData.name)) {
      toast.error("Name should contain only alphabets");
      return;
    }

    if (!/^\d+$/.test(formData.age)) {
      toast.error("Age must be numeric");
      return;
    }

    if (!/^\d+$/.test(formData.phone)) {
      toast.error("Phone must contain only numbers");
      return;
    }

    setFormLoading(true);

    try {
      await api.post("/api/send-international-appointment", {
        ...formData,
        page_url: window.location.href,
      });

      toast.success("Details submitted successfully!");

      setFormData({
        name: "",
        age: "",
        gender: "",
        email: "",
        country: "",
        phone: "",
        speciality: "",
        comments: "",
      });
    } catch {
      toast.error("Submission failed. Try again.");
    }

    setFormLoading(false);
  };

  /* ================= EFFECT ================= */
  useEffect(() => {
    document.querySelectorAll(".progress-card").forEach((card, index) => {
      card.setAttribute("data-sal", "slide-up");
      card.setAttribute("data-sal-duration", "1000");
      card.setAttribute("data-sal-delay", progressData[index]?.delay || 100);
    });
    sal({ threshold: 0.2, once: true });
  }, []);

  return (
    <>
      {formLoading && <DnaOverlayLoader />}

      <section
        className="progress-section speciality-section medical-department-section py-5"
        ref={formRef}
      >
        <div className="container">
          {/* ================= HEADING ================= */}
          <div className="row">
            <div className="col-12">
              <h2
                className="section-title text-capitalize"
                data-sal="slide-up"
                data-sal-duration="1000"
                data-sal-delay="100"
              >
                Expert Medical Departments
              </h2>

              <p
                className="section-subtitle"
                data-sal="slide-up"
                data-sal-duration="1000"
                data-sal-delay="200"
              >
                Comprehensive Care for International Patients
              </p>
            </div>
          </div>

          <div className="row align-items-center">
            {/* ================= LEFT : SLIDER ================= */}
            <div className="col-lg-8 position-relative">
              <Swiper
                navigation={false}
                autoplay={{ delay: 3500, disableOnInteraction: false }}
                loop={true}
                spaceBetween={20}
                modules={[Grid]}
                grid={{ rows: 2, fill: "row" }}
                slidesPerView={4}
                breakpoints={{
                  1200: { slidesPerView: 4, grid: { rows: 2 } },
                  992: { slidesPerView: 3, grid: { rows: 2 } },
                  768: { slidesPerView: 2, grid: { rows: 2 } },
                  0: { slidesPerView: 1, grid: { rows: 1 } },
                }}
                onInit={(swiper) => setTimeout(() => swiper.update(), 50)}
              >
                {progressData.map((item, index) => (
                  <SwiperSlide key={index}>
                    <div
                      className="progress-card"
                      data-sal="slide-up"
                      data-sal-duration="1000"
                      data-sal-delay={item.delay}
                    >
                      <div className="progress-img-wrapper mb-2">
                        <img src={item.img} alt={item.title} />
                      </div>
                      <h2 className="progress-title text-center">
                        {item.title}
                      </h2>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* ================= RIGHT : FORM ================= */}
            <div className="col-lg-4">
              <div
                className="form-card"
                data-sal="slide-up"
                data-sal-duration="1000"
              >
                <div className="form-header">
                  <h2>
                    Personalized Treatment & Cost Estimate for International
                    Patients
                  </h2>
                </div>

                <form className="patient-form" onSubmit={handleSubmit}>
                  {/* NAME */}
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder=" "
                      required
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      maxLength={50}
                    />
                    <label>
                      Name <span className="req">*</span>
                    </label>
                  </div>

                  {/* AGE + GENDER */}
                  <div className="form-row">
                    <div className="form-group">
                      <input
                        type="text"
                        placeholder=" "
                        required
                        name="age"
                        value={formData.age}
                        onChange={handleChange}
                        inputMode="numeric"
                        maxLength={3}
                      />
                      <label>
                        Age <span className="req">*</span>
                      </label>
                    </div>

                    <div className="form-group">
                      <select
                        required
                        onChange={handleChange}
                        name="gender"
                        value={formData.gender}
                      >
                        <option value="" disabled hidden></option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                      <label>
                        Gender <span className="req">*</span>
                      </label>
                    </div>
                  </div>

                  {/* EMAIL */}
                  <div className="form-group">
                    <input
                      type="email"
                      placeholder=" "
                      required
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      maxLength={75}
                    />
                    <label>
                      E-mail <span className="req">*</span>
                    </label>
                  </div>

                  {/* COUNTRY */}
                  <div className="form-group">
                    <select
                      required
                      onChange={handleChange}
                      name="country"
                      value={formData.country}
                    >
                      <option value="" disabled hidden></option>
                      <option value="India">India</option>
                      <option value="USA">USA</option>
                      <option value="UK">UK</option>
                      <option value="Canada">Canada</option>
                      <option value="Australia">Australia</option>
                      <option value="Other">Other</option>
                    </select>
                    <label>
                      Country <span className="req">*</span>
                    </label>
                  </div>

                  {/* PHONE */}
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder=" "
                      required
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      inputMode="numeric"
                      maxLength={12}
                    />
                    <label>
                      Phone <span className="req">*</span>
                    </label>
                  </div>

                  {/* SPECIALITY - NOW DYNAMICALLY LOADED */}
                  <div className="form-group">
                    <select
                      required
                      onChange={handleChange}
                      name="speciality"
                      value={formData.speciality}
                      disabled={loadingSpecialties}
                    >
                      <option value="" disabled hidden></option>
                      {loadingSpecialties ? (
                        <option value="" disabled>Loading specialties...</option>
                      ) : (
                        specialties.map((specialty, index) => (
                          <option key={index} value={specialty}>
                            {specialty}
                          </option>
                        ))
                      )}
                    </select>
                    <label>
                      Choose Speciality <span className="req">*</span>
                    </label>
                  </div>

                  {/* COMMENTS */}
                  <div className="form-group">
                    <textarea
                      rows="2"
                      placeholder=" "
                      required
                      name="comments"
                      value={formData.comments}
                      onChange={handleChange}
                      maxLength={250}
                    ></textarea>
                    <label>
                      Comments <span className="req">*</span>
                    </label>
                  </div>

                  <button type="submit" className="submit-btn">
                    Submit
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MedicalDepartments;